/**
 * src/RpgPricingPage.js
 *
 * VISINVOKE RPG Pricing & Access -- Coming Late 2026.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import { glassSurface } from './config/glassTheme';

const C  = '#be123c';
const CD = '#9f1239';
const CM = '#e11d48';


const FEATURE_GRID = [
  'Persistent campaign progression',
  'Solo party-based RPG structure',
  'Up to four controllable characters',
  'Long-term world continuity',
  'Exploration-focused gameplay',
  'Persistent faction systems',
  'Reputation and consequence systems',
  'Campaign-aware narrative support',
  'Time-based world progression',
  'Structured solo RPG experiences',
  'Unified persistent world ecosystem',
  'Long-form replayability focus',
];

const COMPARE_LEFT = [
  'Temporary sessions',
  'Disposable story loops',
  'Minimal continuity',
  'Isolated AI interactions',
  'Limited progression persistence',
  'Little long-form campaign structure',
];

const COMPARE_RIGHT = [
  'Persistent campaign continuity',
  'Long-term world progression',
  'Returning-world structure',
  'Solo party-based campaigns',
  'Exploration-focused systems',
  'Persistent faction relationships',
  'Time-based world progression',
  'Long-form RPG campaigns',
];

const SECTIONS = [
  {
    title: 'Designed Around Long-Term Replayability',
    body: [
      'VISINVOKE RPG Text Game is planned around persistent progression, evolving campaigns, long-form exploration, world continuity, party development, faction systems, and structured solo RPG play.',
      'The objective is to create a platform users continue returning to over time rather than a temporary AI interaction system.',
    ],
  },
  {
    title: 'Positioned As A Persistent RPG Platform',
    body: [
      'The pricing structure is planned around the idea of an evolving long-term RPG ecosystem rather than isolated AI sessions or disposable prompt interactions.',
      'The focus is long-term replayability, persistent progression, campaign continuity, evolving exploration, world persistence, and structured solo RPG systems.',
      'The goal is creating an RPG platform users continue returning to regularly over time.',
    ],
  },
  {
    title: 'Designed For Deep Solo RPG Campaigns',
    body: [
      'The RPG Text Game is planned primarily around solo campaign play where one user can create a main character, control a party of up to four characters, guide long-term progression, explore evolving regions, interact with factions, survive long-form campaigns, and uncover discoveries across persistent worlds.',
      'The objective is supporting deep long-term solo RPG adventures.',
    ],
  },
  {
    title: 'A World Designed To Continue Evolving',
    body: [
      'The RPG world is planned around continuity where locations persist, discoveries remain meaningful, factions evolve, travel matters, campaign progression continues, time advancement creates pressure, and exploration carries consequence.',
      'The objective is to create a world that feels active beyond isolated play sessions.',
    ],
  },
  {
    title: 'One Of The Largest Planned VISINVOKE Systems',
    body: [
      'VISINVOKE RPG Text Game is planned as one of the largest future VISINVOKE products because it is intended to support daily engagement, long-form replayability, persistent progression, evolving campaigns, unified world continuity, and exploration-focused solo RPG gameplay.',
      'The focus is creating a premium long-term RPG ecosystem users continue returning to over extended periods of time.',
    ],
  },
];

export default function RpgPricingPage() {
  const navigate = useNavigate();

  return (
    <div style={pg.page}>
      <ProductNav />

      {/* Hero */}
      <div style={pg.hero}>
        <div style={pg.heroGlow} />
        <div style={pg.heroInner}>
          <div style={pg.badge}>Planned For Late 2026</div>
          <h1 style={pg.heroTitle}>A Persistent Solo RPG Platform Designed For Long-Term Play</h1>
          <p style={pg.heroSub}>
            VISINVOKE RPG Text Game is planned as a long-form solo RPG experience focused on persistent worlds, evolving campaigns, exploration, faction systems, party progression, continuity-driven storytelling, and long-term world interaction.
          </p>
        </div>
      </div>

      {/* Primary plan card */}
      <div style={pg.planSection}>
        <div style={pg.planCard}>
          <div style={pg.planBadge}>Coming Late 2026</div>
          <div style={pg.planName}>VISINVOKE RPG Access</div>
          <div style={pg.planPrice}>$9.99 <span style={pg.planPriceSub}>USD / month</span></div>
          <p style={pg.planDesc}>Designed as a persistent long-term RPG platform rather than a disposable one-session AI experience.</p>
          <div style={pg.planDivider} />
          <div style={pg.planFeatures}>
            {FEATURE_GRID.map((f, i) => (
              <div key={i} style={pg.planFeat}>
                <span style={pg.planFeatDot} />
                <span>{f}</span>
              </div>
            ))}
          </div>
          <div style={pg.planBtns}>
            <button style={pg.btnPrimary} onClick={() => navigate('/dashboard/rpg')}>Return To RPG Overview</button>
            <button style={pg.btnSecondary} onClick={() => navigate('/dashboard')}>Return To Dashboard</button>
          </div>
          <p style={pg.waitlistNote}>Waitlist and early access details will be announced closer to launch.</p>
        </div>
      </div>

      {/* Comparison grid */}
      <div style={pg.compareSection}>
        <div style={pg.compareInner}>
          <h2 style={pg.compareTitle}>Built Beyond Disposable AI RPG Sessions</h2>
          <p style={pg.compareSub}>Many AI RPG systems focus on temporary conversations, short-lived adventures, or isolated prompt interactions. VISINVOKE RPG Text Game is planned around persistent continuity, evolving campaigns, long-term progression, and returning-world structure.</p>
          <div style={pg.compareGrid}>
            <div style={pg.compareCol}>
              <div style={pg.compareColHeader}>Typical AI RPG Systems</div>
              {COMPARE_LEFT.map((item, i) => (
                <div key={i} style={pg.compareItemLeft}>
                  <span style={{ color: '#6b7280', marginRight: 8 }}>&#x2715;</span>{item}
                </div>
              ))}
            </div>
            <div style={{ ...pg.compareCol, ...pg.compareColRight }}>
              <div style={{ ...pg.compareColHeader, color: CM }}>VISINVOKE RPG Text Game</div>
              {COMPARE_RIGHT.map((item, i) => (
                <div key={i} style={pg.compareItemRight}>
                  <span style={{ color: CM, marginRight: 8 }}>&#x2713;</span>{item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content sections */}
      <div style={pg.sectionsWrap}>
        {SECTIONS.map((sec, i) => (
          <div key={i} style={{ ...pg.section }}>
            <h2 style={pg.secTitle}>{sec.title}</h2>
            {sec.body.map((p, j) => <p key={j} style={pg.secBody}>{p}</p>)}
          </div>
        ))}
      </div>

      {/* Footer CTA */}
      <div style={pg.footer}>
        <div style={pg.footerInner}>
          <div style={pg.badge}>Planned For Late 2026</div>
          <h2 style={pg.footerTitle}>RPG Pricing &amp; Access</h2>
          <p style={pg.footerSub}>A persistent long-term RPG platform. Part of the growing VISINVOKE ecosystem.</p>
          <div style={pg.heroBtns}>
            <button style={pg.btnPrimary} onClick={() => navigate('/dashboard/rpg')}>Return To RPG Overview</button>
            <button style={pg.btnSecondary} onClick={() => navigate('/dashboard')}>Return To Dashboard</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const pg = {
  page: { minHeight: '100vh', background: '#09010a', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },

  hero: { position: 'relative', overflow: 'hidden', background: 'linear-gradient(160deg, #110010 0%, #1a0012 40%, #0e000c 70%, #07000a 100%)', borderBottom: `1px solid ${C}33`, padding: '72px 0 64px' },
  heroGlow: { position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 800, height: 500, borderRadius: '50%', background: `radial-gradient(ellipse, ${C}20 0%, transparent 70%)`, pointerEvents: 'none' },
  heroInner: { position: 'relative', maxWidth: 820, margin: '0 auto', padding: '0 48px', textAlign: 'center', zIndex: 1 },

  badge: { ...glassSurface(CD, C, CM), display: 'inline-block', borderRadius: 20, padding: '6px 20px', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 28 },
  heroTitle: { fontSize: 40, fontWeight: 900, color: '#f8f4ff', lineHeight: 1.2, marginBottom: 22, letterSpacing: '-0.5px', textShadow: `0 0 60px ${C}44` },
  heroSub: { fontSize: 17, color: 'rgba(255,255,255,0.58)', lineHeight: 1.8, maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' },

  planSection: { maxWidth: 720, margin: '48px auto', padding: '0 40px' },
  planCard: { background: `linear-gradient(160deg, #160010 0%, #0f0009 100%)`, border: `2px solid ${C}44`, borderRadius: 20, padding: '44px 48px', boxShadow: `0 0 80px ${C}22, 0 20px 60px rgba(0,0,0,0.6)` },
  planBadge: { ...glassSurface(CD, C, CM), display: 'inline-block', borderRadius: 16, padding: '4px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 18 },
  planName: { fontSize: 28, fontWeight: 900, color: '#f8f4ff', marginBottom: 16 },
  planPrice: { fontSize: 52, fontWeight: 900, color: CM, lineHeight: 1, marginBottom: 16 },
  planPriceSub: { fontSize: 18, fontWeight: 500, color: 'rgba(255,255,255,0.45)' },
  planDesc: { fontSize: 15, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: 24 },
  planDivider: { height: 1, background: `${C}33`, marginBottom: 24 },
  planFeatures: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', marginBottom: 32 },
  planFeat: { display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'rgba(255,255,255,0.68)' },
  planFeatDot: { width: 6, height: 6, borderRadius: '50%', background: CM, flexShrink: 0 },
  planBtns: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 },
  waitlistNote: { fontSize: 12, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 },

  btnPrimary: { ...glassSurface(CD, C, CM), borderRadius: 10, padding: '13px 26px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnSecondary: { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 10, padding: '13px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },

  compareSection: { background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '48px 0' },
  compareInner: { maxWidth: 960, margin: '0 auto', padding: '0 40px' },
  compareTitle: { fontSize: 28, fontWeight: 800, color: '#f0ebff', marginBottom: 16, textAlign: 'center' },
  compareSub: { fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, marginBottom: 36, textAlign: 'center', maxWidth: 800, marginLeft: 'auto', marginRight: 'auto' },
  compareGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  compareCol: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 12 },
  compareColRight: { border: `1px solid ${C}33`, background: `${C}08` },
  compareColHeader: { fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 },
  compareItemLeft: { fontSize: 14, color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center' },
  compareItemRight: { fontSize: 14, color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center' },

  sectionsWrap: { maxWidth: 960, margin: '0 auto', padding: '0 40px 48px' },
  section: { borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '36px 0' },
  secTitle: { fontSize: 24, fontWeight: 800, color: '#f0ebff', marginBottom: 16 },
  secBody: { fontSize: 15, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, marginBottom: 12 },

  footer: { background: 'linear-gradient(160deg, #0e000c 0%, #180010 100%)', borderTop: `1px solid ${C}33`, padding: '56px 48px', textAlign: 'center' },
  footerInner: { maxWidth: 720, margin: '0 auto' },
  footerTitle: { fontSize: 32, fontWeight: 900, color: '#f8f4ff', marginBottom: 14, marginTop: 16 },
  footerSub: { fontSize: 16, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, marginBottom: 36 },
  heroBtns: { display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' },
};
